// ─── Shift Dashboard tiles (z8nrz7fbcn / z8nrz7fbcr) ─────────────────────────
//
// The tile catalog for the Shift Dashboard. Ids are PERMANENT: a shared view
// stores them in its `widgets` list, so renaming one silently drops the tile
// from every view that has it. Titles and order here are the display defaults;
// counts, status and rows always come from the server.
//
// Pure, so the rules are unit-tested (vitest here runs `.ts` only).

export type ShiftStatus = 'critical' | 'attention' | 'ready' | 'info'

export interface ShiftTileDef {
  id: string
  title: string
  /** What the count means, for the Customize list. */
  description: string
}

export const SHIFT_TILES: readonly ShiftTileDef[] = [
  { id: 'procedures', title: 'Procedures', description: 'Procedures running or due this shift, blocked and overdue first.' },
  { id: 'equipment', title: 'Equipment', description: 'Equipment in scope: unavailable, cleaning, calibration and alarms.' },
  { id: 'staff_on_shift', title: 'Staff on shift', description: 'People rostered on this shift, assigned and still available.' },
  { id: 'who_is_out', title: 'Who is out', description: 'People unavailable during this shift.' },
  { id: 'inventory', title: 'Inventory', description: 'Usable stock, by item and location.' },
  { id: 'stock_shortages', title: 'Stock shortages', description: "Material this shift's procedures need and do not have." },
  { id: 'low_stock', title: 'Low stock', description: 'Items below their configured minimum.' },
  { id: 'product_custody', title: 'Product and custody', description: 'Products in custody, awaiting release or shipment.' },
  { id: 'quality_safety', title: 'Quality and safety', description: 'Open CAPA and quality events, high severity first.' },
  { id: 'reviews_release', title: 'Reviews and release', description: 'Forms and releases waiting for review this shift.' },
  { id: 'action_items', title: 'Action items', description: 'Steps and maintenance tasks due this shift, overdue first.' },
  { id: 'cleaning', title: 'Cleaning', description: 'Rooms due, expiring or expired for cleaning.' },
]

const BY_ID = new Map(SHIFT_TILES.map((t) => [t.id, t]))

export function shiftTile(id: string): ShiftTileDef | undefined {
  return BY_ID.get(id)
}

/** The order a view with no saved tiles opens in: every tile, catalog order. */
export const DEFAULT_SHIFT_TILE_IDS: string[] = SHIFT_TILES.map((t) => t.id)

/**
 * A stored view's tiles, made renderable: anything that is not a shift tile
 * (a Home widget saved before the tile catalog existed, a hand-edited row) and
 * repeats are dropped. An EMPTY result means "the default set" — a view must
 * never open blank because its stored list was all unknown ids.
 */
export function coerceShiftTileIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  const seen = new Set<string>()
  const out: string[] = []
  for (const entry of raw) {
    const id = typeof entry === 'string' ? entry : typeof entry === 'object' && entry ? (entry as { id?: unknown }).id : null
    if (typeof id !== 'string' || seen.has(id) || !BY_ID.has(id)) continue
    seen.add(id)
    out.push(id)
  }
  return out
}

/** The tiles a view shows, in its order. */
export function viewTileIds(stored: unknown): string[] {
  const ids = coerceShiftTileIds(stored)
  return ids.length > 0 ? ids : DEFAULT_SHIFT_TILE_IDS
}

/** The shape stored on the view. Size is fixed: every shift tile is one cell. */
export function tileIdsToWidgets(ids: string[]): { id: string; size: string }[] {
  return coerceShiftTileIds(ids).map((id) => ({ id, size: 'S' }))
}

/** Moves the tile at `from` by `delta` places; same array back for a move off either end. */
export function moveTile(ids: string[], from: number, delta: number): string[] {
  const to = from + delta
  if (from < 0 || from >= ids.length || to < 0 || to >= ids.length) return ids
  const next = [...ids]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

/** Shows or hides one tile; a shown tile goes to the end. */
export function toggleTile(ids: string[], id: string, shown: boolean): string[] {
  const has = ids.includes(id)
  if (shown === has || !BY_ID.has(id)) return ids
  return shown ? [...ids, id] : ids.filter((x) => x !== id)
}

// ─── Status ──────────────────────────────────────────────────────────────────

/** Always a word beside the colour: status is never colour alone (AC). */
export const STATUS_META: Record<ShiftStatus, { label: string; chip: string; dot: string; rank: number }> = {
  critical: { label: 'Critical', chip: 'bg-red-50 text-red-700 ring-red-200', dot: 'bg-red-600', rank: 0 },
  attention: { label: 'Attention', chip: 'bg-amber-50 text-amber-800 ring-amber-200', dot: 'bg-amber-600', rank: 1 },
  ready: { label: 'Ready', chip: 'bg-green-50 text-green-700 ring-green-200', dot: 'bg-green-600', rank: 2 },
  info: { label: 'Info', chip: 'bg-blue-50 text-blue-700 ring-blue-200', dot: 'bg-blue-600', rank: 3 },
}

export function isShiftStatus(v: unknown): v is ShiftStatus {
  return v === 'critical' || v === 'attention' || v === 'ready' || v === 'info'
}

// ─── Drill-down ──────────────────────────────────────────────────────────────

export const ALL_FACET = '__all'

/** Rows in the chosen quick-filter chip; `ALL_FACET` is every row. */
export function rowsInFacet<R extends { facets: string[] }>(rows: R[], facet: string): R[] {
  return facet === ALL_FACET ? rows : rows.filter((r) => r.facets.includes(facet))
}

/** Case-insensitive search over a row's cell text and detail title. */
export function searchRows<R extends { cells: Record<string, unknown>; detail: { title: string } }>(
  rows: R[],
  query: string,
): R[] {
  const q = query.trim().toLowerCase()
  if (!q) return rows
  return rows.filter((r) => {
    if (r.detail.title.toLowerCase().includes(q)) return true
    return Object.values(r.cells).some((c) => cellText(c).toLowerCase().includes(q))
  })
}

/** A cell as plain text, whatever shape the server sent. */
export function cellText(cell: unknown): string {
  if (cell === null || cell === undefined) return ''
  if (typeof cell === 'string' || typeof cell === 'number') return String(cell)
  if (typeof cell === 'object' && typeof (cell as { text?: unknown }).text === 'string') return (cell as { text: string }).text
  return ''
}

/**
 * One page of rows, 1-based. A page past the end (the list shrank under a new
 * filter or a refresh) clamps to the last page rather than showing nothing.
 */
export function pageSlice<R>(rows: R[], page: number, size: number): { rows: R[]; page: number; pages: number } {
  const pages = Math.max(1, Math.ceil(rows.length / size))
  const current = Math.min(Math.max(1, page), pages)
  return { rows: rows.slice((current - 1) * size, current * size), page: current, pages }
}

/** "Showing 6 of 7" for the Attention panel; nothing when everything is shown. */
export function showingLabel(shown: number, total: number): string | null {
  return total > shown ? `Showing ${shown} of ${total}` : null
}
